import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Alert from "@mui/material/Alert";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import {
  advanceSearch,
  advanceSearchMyFavorites,
  getDiagramAvailability,
  getMyFavorites,
  removeMyFavorites,
  updateMyFavorites,
} from "../../actions";
import { getShortName, sortByProperty } from "../../utils/accessMapFunctions";
import businessFlowsTheme, { brand, radius } from "../../theme/businessFlowsTheme";
import FilterPanel from "./FilterPanel";
import ResultCard from "./ResultCard";
import DetailsPanel from "../app/DetailsPanel";
import { EMPTY_DETAILS, loadObjectDetails } from "../app/objectDetails";
import { useViewConfig, useViewConfigUnavailable } from "../app/ViewConfigContext";
import { isDatabaseUnreachable, unreachableMessage } from "../../utils/databaseOffline";
import { attributeLabel, setting, typesIn } from "../../utils/viewConfig";
import "../auth/auth.css";

function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const panelSx = {
  borderRadius: radius.surface,
  border: `1px solid ${brand.line}`,
  bgcolor: "#fff",
  overflow: "hidden",
  minHeight: 0,
};

export default function AdvanceSearch(props) {
  const history = useHistory();
  const location = useLocation();
  // Deep link: ?q=<name>&guid=<object GUID>[&type=<type>] opens the search on that object.
  const deepLink = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return { q: params.get("q") || "", guid: params.get("guid") || "", type: params.get("type") || "" };
  }, [location.search]);
  const wide = useMediaQuery(businessFlowsTheme.breakpoints.up("lg"));
  const medium = useMediaQuery(businessFlowsTheme.breakpoints.up("md"));

  // Which types users can filter by, page size, sort fields and the starting
  // type come from the view configuration and update on the fly.
  const config = useViewConfig();
  // Why the filters are empty: nothing configured for search, or the database
  // couldn't be asked at all. Only the first is something an admin fixes.
  const configUnavailable = useViewConfigUnavailable();
  const pageSize = setting(config, "search.pageSize");
  const sortFields = setting(config, "search.sortFields");
  const types = useMemo(
    () => typesIn(config, "inSearch").map((t) => ({ name: t.objectType, label: t.label, colour: t.colour })),
    [config]
  );
  const [sortField, setSortField] = useState("");
  const orderBy = sortFields.includes(sortField) ? sortField : sortFields[0] || "name";
  const [query, setQuery] = useState(deepLink.q);
  const [checkedTypes, setCheckedTypes] = useState([]);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [sort, setSort] = useState("ASC");
  const [page, setPage] = useState(1);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // The last search failed because the database could not be reached.
  const [searchUnavailable, setSearchUnavailable] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [selected, setSelected] = useState(null);
  // The reader asked for this object themselves (a card, or a link that named
  // it). The panel fills itself with the first result too, and that must not
  // push a drawer over a phone's screen.
  const [chosen, setChosen] = useState(false);
  const [details, setDetails] = useState(EMPTY_DETAILS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const requestId = useRef(0);
  const listRef = useRef(null);

  const debouncedQuery = useDebounced(query.trim(), 300);

  // With no search term the page starts on the type an admin chose under
  // Views -> Settings, or on every type. A deep link brings its own object.
  const defaultFilterApplied = useRef(false);
  useEffect(() => {
    if (defaultFilterApplied.current || !config.loaded) return;
    defaultFilterApplied.current = true;
    // A link that names a type starts on that type - Home's library panel
    // sends one per object type, so "Process Group" on Home opens the search
    // already filtered to process groups.
    if (deepLink.type && !deepLink.guid && types.some((t) => t.name === deepLink.type)) {
      setCheckedTypes([deepLink.type]);
      return;
    }
    if (deepLink.q || deepLink.guid) return;
    const start = setting(config, "search.defaultType");
    if (start && types.some((t) => t.name === start)) setCheckedTypes([start]);
  }, [config, types, deepLink]);

  // Back to the first page whenever the question changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, checkedTypes, favoritesOnly, sort, orderBy, pageSize]);

  // A type an admin removes from the search filter (or that drops out for
  // any other reason) must not go on silently filtering results the reader
  // can no longer see or clear from the panel.
  useEffect(() => {
    setCheckedTypes((current) => {
      const names = new Set(types.map((t) => t.name));
      const kept = current.filter((name) => names.has(name));
      return kept.length === current.length ? current : kept;
    });
  }, [types]);

  useEffect(() => {
    const id = ++requestId.current;
    const skip = (page - 1) * pageSize;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const favoritesResponse = await getMyFavorites();
        const response = favoritesOnly
          ? await advanceSearchMyFavorites("name", favoritesResponse, skip, pageSize, orderBy, sort, checkedTypes)
          // "search" says which screen is asking: the server refuses these
          // calls where Advanced search is switched off in Console -> Features,
          // while quick search (view=quick) keeps working.
          : await advanceSearch("name", debouncedQuery, skip, pageSize, orderBy, sort, checkedTypes, "search");
        if (id !== requestId.current) return;

        setFavorites(Array.isArray(favoritesResponse?.content) ? favoritesResponse.content : []);
        if (!response?.success) {
          setResults([]);
          setTotal(0);
          // A database that can't be reached said so itself, naming the
          // database and who can fix it: show that, not "no results".
          setError(
            isDatabaseUnreachable(response)
              ? unreachableMessage(response)
              : response?.msg || "Search isn't available right now. Try again in a moment."
          );
          setSearchUnavailable(isDatabaseUnreachable(response));
          return;
        }
        setSearchUnavailable(false);
        // One request tells which results have a diagram in any format.
        const items = response.content || [];
        const availability = await getDiagramAvailability(items.map((item) => item.GUID));
        const withDiagrams = items.map((item) => ({ ...item, formats: availability[item.GUID] || [] }));
        if (id !== requestId.current) return;
        setResults(withDiagrams);
        setTotal(response.total || 0);
        if (listRef.current) listRef.current.scrollTop = 0;
      } catch (err) {
        if (id !== requestId.current) return;
        setResults([]);
        setTotal(0);
        setError("Search isn't available right now. Try again in a moment.");
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    })();
  }, [debouncedQuery, checkedTypes, favoritesOnly, sort, page, refresh, pageSize, orderBy]);

  const showDetails = useCallback(async (item) => {
    setSelected(item);
    setDetails({ ...EMPTY_DETAILS, loading: true });
    // Always an answer, even for an object that can't be read: the panel says
    // so instead of waiting on its skeletons.
    const loaded = await loadObjectDetails(item.GUID);
    setSelected((current) => {
      if (current?.GUID === item.GUID) {
        setDetails({
          ...loaded,
          occurrences: [...loaded.occurrences].sort(sortByProperty("name")),
          relationships: [...loaded.relationships].sort(sortByProperty("name")),
        });
      }
      return current;
    });
  }, []);

  // Opened from a diagram, map or explorer: show that object's details right away.
  useEffect(() => {
    setQuery(deepLink.q);
    if (deepLink.guid) {
      setChosen(true);
      showDetails({ GUID: deepLink.guid, name: deepLink.q, Type: deepLink.type || undefined });
    }
  }, [deepLink, showDetails]);

  // Beside the results, the panel shows the first result of the page rather
  // than a quarter of the screen reading "No result selected". It is the same
  // object, read the same way, and the card it belongs to is marked as
  // selected - so picking another stays the reader's move. A page with no
  // results at all leaves the panel empty, which is then the truth.
  useEffect(() => {
    if (deepLink.guid) return;
    if (!results.length) {
      if (selected) {
        setSelected(null);
        setChosen(false);
        setDetails(EMPTY_DETAILS);
      }
      return;
    }
    // Only where the panel is on the page: on a phone it is a drawer, and
    // filling one nobody opened would cost two requests for nothing.
    if (!wide) return;
    if (selected && results.some((item) => item.GUID === selected.GUID)) return;
    setChosen(false);
    showDetails(results[0]);
  }, [results, selected, wide, deepLink.guid, showDetails]);

  const toggleStar = async (item) => {
    const starred = favorites.includes(item.GUID);
    setFavorites((list) => (starred ? list.filter((g) => g !== item.GUID) : [...list, item.GUID]));
    if (starred) {
      await removeMyFavorites(item.GUID);
      if (favoritesOnly) setRefresh((n) => n + 1);
    } else {
      await updateMyFavorites(item.GUID);
    }
  };

  const openExplorer = (item) => {
    props.setNodeDatum({
      id: item.GUID,
      guid: item.GUID,
      shortName: getShortName(item.name),
      fullName: item.name,
      type: item.Type,
      description: item.Description,
      children: [],
      isLink: false,
      url: item.URL,
      style: { background: "#FFFFFF" },
    });
    history.push(`${props.path}/object/${item.GUID}`);
  };

  const pageCount = Math.ceil(total / pageSize);
  const activeFilterCount = checkedTypes.length + (favoritesOnly ? 1 : 0);

  // What to say when there are no types to filter by. While the database
  // can't be reached, nothing is known about its types, so the panel must not
  // send anyone to Views over it.
  const unavailableMessage = configUnavailable
    ? unreachableMessage(configUnavailable)
    : searchUnavailable
    ? error
    : "";

  const filterPanel = (
    <FilterPanel
      types={types}
      checkedTypes={checkedTypes}
      onTypesChange={setCheckedTypes}
      favoritesOnly={favoritesOnly}
      onFavoritesChange={setFavoritesOnly}
      unavailableMessage={unavailableMessage}
    />
  );

  const detailsPanel = (
    <DetailsPanel
      key={selected?.GUID || "none"}
      selected={selected}
      details={details}
      path={props.path}
      onClose={wide ? null : () => setChosen(false)}
      onOpenExplorer={(item) => openExplorer(item)}
      onOpenDiagram={(item) => window.open(`${props.path}/map/${encodeURIComponent(item.GUID)}`, "_blank", "noopener")}
    />
  );

  return (
    <ThemeProvider theme={businessFlowsTheme}>
      <Box
        className="bfx"
        sx={{
          flex: 1,
          minHeight: 0,
          height: "100%",
          bgcolor: "background.default",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            maxWidth: 1600,
            mx: "auto",
            boxSizing: "border-box",
            p: { xs: 1.5, sm: 2, md: 2.5 },
            display: "grid",
            gap: { xs: 1.5, md: 2 },
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "260px minmax(0, 1fr)",
              lg: "270px minmax(0, 1fr) 360px",
            },
          }}
        >
          {medium && <Paper elevation={0} sx={panelSx}>{filterPanel}</Paper>}

          {/* Results */}
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                fullWidth
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={favoritesOnly ? "Search is paused while showing favourites" : "Search by name"}
                disabled={favoritesOnly}
                inputProps={{ "aria-label": "Search by name" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  endAdornment: query ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setQuery("")} aria-label="Clear search">
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  sx: { bgcolor: "#fff", borderRadius: radius.card, height: 48 },
                }}
              />
              {!medium && (
                <Badge badgeContent={activeFilterCount} color="secondary" overlap="circular">
                  <IconButton
                    onClick={() => setFiltersOpen(true)}
                    aria-label="Filters"
                    sx={{ border: `1px solid ${brand.line}`, bgcolor: "#fff", borderRadius: radius.card, width: 48, height: 48 }}
                  >
                    <TuneRoundedIcon />
                  </IconButton>
                </Badge>
              )}
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{ mt: 1.5, mb: 1.25, minHeight: 32, flexWrap: "wrap", rowGap: 0.75 }}
            >
              <Typography variant="body2" color="text.secondary" aria-live="polite">
                {loading ? "Searching…" : `${total.toLocaleString()} ${total === 1 ? "result" : "results"}`}
                {favoritesOnly && !loading ? " in your favourites" : ""}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 0.75 }}>
                {sortFields.length > 1 && (
                  <TextField
                    select
                    size="small"
                    fullWidth={false}
                    value={orderBy}
                    onChange={(e) => setSortField(e.target.value)}
                    inputProps={{ "aria-label": "Sort by" }}
                    sx={{ width: { xs: 128, sm: 160 }, "& .MuiInputBase-root": { bgcolor: "#fff", height: 32 } }}
                  >
                    {sortFields.map((field) => (
                      <MenuItem key={field} value={field}>
                        {attributeLabel(field)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={sort}
                  onChange={(e, v) => v && setSort(v)}
                  aria-label="Sort order"
                  sx={{
                    bgcolor: "#fff",
                    "& .MuiToggleButton-root": { px: 1.25, py: 0.25, textTransform: "none", fontWeight: 600, fontSize: "0.8rem" },
                    "& .Mui-selected": { bgcolor: `${brand.ink} !important`, color: "#fff !important" },
                  }}
                >
                  <ToggleButton value="ASC">A–Z</ToggleButton>
                  <ToggleButton value="DESC">Z–A</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>

            {error && (
              <Alert severity="error" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={() => setRefresh((n) => n + 1)}>Retry</Button>}>
                {error}
              </Alert>
            )}

            <Box ref={listRef} sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: { md: 0.5 } }}>
              {loading && results.length === 0 ? (
                <Stack spacing={1.25}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={112} sx={{ borderRadius: radius.card }} />
                  ))}
                </Stack>
              ) : results.length === 0 && !error ? (
                <Stack alignItems="center" spacing={1.25} sx={{ py: 8, textAlign: "center" }}>
                  <SearchOffRoundedIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                  <Typography sx={{ fontWeight: 650 }}>
                    {favoritesOnly ? "No favourites yet" : "No matches"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 340 }}>
                    {favoritesOnly
                      ? "Star a result to find it here next time."
                      : "Try a shorter name or remove some type filters."}
                  </Typography>
                  {activeFilterCount > 0 && (
                    <Button
                      size="small"
                      onClick={() => {
                        setCheckedTypes([]);
                        setFavoritesOnly(false);
                      }}
                    >
                      Clear filters
                    </Button>
                  )}
                </Stack>
              ) : (
                <Stack spacing={1.25} sx={{ opacity: loading ? 0.55 : 1, transition: "opacity .15s ease", pb: 1 }}>
                  {results.map((item) => (
                    <ResultCard
                      key={item.GUID}
                      item={item}
                      path={props.path}
                      selected={selected?.GUID === item.GUID}
                      starred={favorites.includes(item.GUID)}
                      onSelect={() => {
                        setChosen(true);
                        showDetails(item);
                      }}
                      onToggleStar={() => toggleStar(item)}
                      onOpenExplorer={() => openExplorer(item)}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            {pageCount > 1 && (
              <Stack alignItems="center" sx={{ pt: 1.25 }}>
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  shape="rounded"
                  size={medium ? "medium" : "small"}
                  siblingCount={medium ? 1 : 0}
                  sx={{ "& .Mui-selected": { bgcolor: `${brand.ink} !important`, color: "#fff" } }}
                />
              </Stack>
            )}
          </Box>

          {wide && <Paper elevation={0} sx={panelSx}>{detailsPanel}</Paper>}
        </Box>

        {!medium && (
          <Drawer
            anchor="left"
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            PaperProps={{ className: "bfx", sx: { width: "min(320px, 88vw)" } }}
          >
            {filterPanel}
            <Box sx={{ p: 2, borderTop: `1px solid ${brand.line}` }}>
              <Button variant="contained" fullWidth onClick={() => setFiltersOpen(false)}>
                Show {total.toLocaleString()} {total === 1 ? "result" : "results"}
              </Button>
            </Box>
          </Drawer>
        )}

        {!wide && (
          <Drawer
            anchor={medium ? "right" : "bottom"}
            open={Boolean(selected) && chosen}
            onClose={() => setChosen(false)}
            PaperProps={{
              className: "bfx",
              sx: medium
                ? { width: 400 }
                : { height: "80vh", borderTopLeftRadius: 20, borderTopRightRadius: 20 },
            }}
          >
            {detailsPanel}
          </Drawer>
        )}
      </Box>
    </ThemeProvider>
  );
}
